if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.cartDrawer = document.querySelector('cart-drawer');

        if (theme.settings.afterAtc !== 'no-js') {
          this.form.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      this.submitBtn = this.querySelector('[name="add"]');

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const pairingButton = evt.submitter?.matches('[data-mse-pairing-submit]') ? evt.submitter : null;
      const pairingWidget = pairingButton?.closest('mse-product-pairings');
      const selectedExtras = pairingWidget ? [...pairingWidget.querySelectorAll('[data-mse-pairing-select]:checked')].filter(input => !input.closest('.mse-pairing-card').hidden) : [];
      if (pairingButton && selectedExtras.length === 0) {
        this.submitBtn.removeAttribute('aria-disabled');
        this.submitBtn.classList.remove('is-loading');
        return;
      }
      if (pairingButton) pairingButton.disabled = true;
      const mainVariantId = Number(formData.get('id'));
      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      if (pairingButton) {
        const properties = {};
        for (const [key, value] of formData.entries()) {
          const match = key.match(/^properties\[(.+)\]$/);
          if (match && typeof value === 'string') properties[match[1]] = value;
        }
        const mainItem = { id: mainVariantId, quantity: Number(formData.get('quantity') || 1), properties };
        if (formData.get('selling_plan')) mainItem.selling_plan = formData.get('selling_plan');
        const items = [mainItem, ...selectedExtras.map(input => ({ id: Number(input.value), quantity: 1, properties: { _mse_recommendation:'product-pairing', _mse_recommendation_for:pairingWidget.dataset.seedId } }))];
        fetchRequestOpts.headers['Content-Type'] = 'application/json';
        fetchRequestOpts.body = JSON.stringify({ items, sections: sections.split(','), sections_url:window.location.pathname });
      }

      let pairingRequestStarted = false;
      let pairingResponseConfirmed = false;
      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        pairingRequestStarted = Boolean(pairingButton);
        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        pairingResponseConfirmed = true;
        let data = await response.json();
        if (response.ok && pairingButton && Array.isArray(data.items)) {
          const mainAdded = data.items.find(item => Number(item.variant_id || item.id) === mainVariantId);
          data = { ...data, variant_id: mainVariantId, quantity: mainAdded?.quantity || Number(formData.get('quantity') || 1) };
          pairingWidget.refreshCards(selectedExtras);
          pairingWidget.update();
        }
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);
        document.dispatchEvent(new CustomEvent('mse:cart:item-added', {
          detail: { source: this.submitBtn, variantId: data.variant_id }
        }));

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Recover HTML omitted by cart integrations after a successful add.
          const sectionIds = sections.split(',');
          if (sectionIds.some((id) => typeof data.sections?.[id] !== 'string')) {
            try {
              const url = new URL(window.location.href);
              url.searchParams.delete('section_id');
              url.searchParams.set('sections', sections);
              const renderedResponse = await fetch(url.toString(), {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
                cache: 'no-store'
              });
              if (!renderedResponse.ok) throw new Error(renderedResponse.status);
              data.sections = await renderedResponse.json();
              if (sectionIds.some((id) => typeof data.sections?.[id] !== 'string')) {
                throw new Error(theme.strings.cartError);
              }
            } catch (error) {
              window.location.href = theme.routes.cart;
              return;
            }
          }

          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        if (pairingButton && pairingRequestStarted && !pairingResponseConfirmed) {
          pairingWidget.dataset.uncertain = 'true';
          this.setErrorMsgState(pairingWidget.dataset.uncertainText);
        }
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg?.textContent
          }
        }));

        if (this.cartDrawer) this.cartDrawer.refresh(true);
      } finally {
        if (pairingButton) pairingWidget.update();
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble) {
        cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}


